import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, Trash2, Clock, CircleCheckBig } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import Result from "@/pages/flow/Result";

export default function DecisionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [wouldAgain, setWouldAgain] = useState(null);
  const [gotRight, setGotRight] = useState("");
  const [missed, setMissed] = useState("");

  const AGAIN = [
    { key: "yes", label: t("detail.againYes") },
    { key: "unsure", label: t("detail.againUnsure") },
    { key: "no", label: t("detail.againNo") },
  ];

  const OUTCOMES = [
    { key: "better", label: t("detail.outcomeBetter") },
    { key: "expected", label: t("detail.outcomeExpected") },
    { key: "worse", label: t("detail.outcomeWorse") },
  ];

  const load = () => {
    api.get(`/decisions/${id}`).then((r) => setD(r.data)).catch((e) => {
      if (e?.response?.status === 401) navigate("/decisions");
      else setNotFound(true);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const markStep = async () => {
    try { await api.post(`/decisions/${id}/next-step`, { next_step: d.analysis.next_action }); toast.success(t("toast.markedNextStep")); load(); }
    catch (e) { toast.error(t("toast.couldNotUpdate")); }
  };
  const schedule = async (when) => {
    try { await api.post(`/decisions/${id}/review-schedule`, { when }); toast.success(t("toast.reviewScheduled")); setReviewOpen(false); load(); }
    catch (e) { toast.error(t("toast.couldNotSchedule")); }
  };
  const submitReview = async () => {
    try { await api.post(`/decisions/${id}/review`, { outcome, got_right: gotRight, missed, would_again: wouldAgain }); toast.success(t("toast.thanksReflection")); setOutcome(null); setWouldAgain(null); setGotRight(""); setMissed(""); load(); }
    catch (e) { toast.error(t("toast.couldNotSave")); }
  };
  const remove = async () => {
    try { await api.delete(`/decisions/${id}`); toast.success(t("toast.deleted")); navigate("/decisions"); }
    catch (e) { toast.error(t("toast.couldNotDelete")); }
  };

  if (loading) return <AppShell><div className="mt-6 h-64 animate-pulse rounded-2xl bg-decide-mist" /></AppShell>;
  if (notFound || !d) return <AppShell><div className="mt-20 text-center text-decide-slate">{t("detail.notFound")}</div></AppShell>;

  const reviewDue = d.review_at && !d.review && new Date(d.review_at) <= new Date();

  return (
    <AppShell showNav={true} right={
      <button data-testid="delete-decision" onClick={remove} aria-label="Delete" className="text-decide-mute hover:text-decide-danger"><Trash2 size={18} /></button>
    }>
      <button data-testid="back-btn" onClick={() => navigate("/decisions")} className="btn-ghost !px-0 mb-2"><ArrowLeft size={18} /> {t("decisions.title")}</button>

      {d.next_step_done ? (
        <div className="mb-5 rounded-2xl border border-decide-forest/30 bg-decide-forestLight p-4 flex items-start gap-2.5" data-testid="next-step-done">
          <CircleCheckBig size={18} className="mt-0.5 shrink-0 text-decide-forest" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-decide-forest">{t("detail.yourNextStep")}</p>
            <p className="text-[15px] text-decide-ink mt-0.5">{d.next_step}</p>
          </div>
        </div>
      ) : null}

      {!d.review && (
        <div className="mb-5 rounded-2xl bg-decide-panel p-5 text-white" data-testid="review-prompt">
          <p className="text-[11px] font-bold uppercase tracking-wider text-decide-panelaccent">{reviewDue ? t("detail.timeToReview") : t("detail.recordOutcome")}</p>
          <p className="mt-2 text-lg font-medium">{t("detail.howTurnOut")}</p>
          <p className="mt-1 text-[13px] text-white/70">{t("detail.howTurnOutSub")}</p>
          <div className="mt-4 space-y-2">
            {OUTCOMES.map((o) => (
              <button key={o.key} data-testid={`outcome-${o.key}`} onClick={() => setOutcome(o.key)} className={`w-full rounded-xl px-4 py-3 text-left text-[15px] font-medium transition-colors ${outcome === o.key ? "bg-decide-forest" : "bg-decide-surface/10 hover:bg-decide-surface/15"}`}>
                {o.label}
              </button>
            ))}
          </div>
          {outcome && (
            <div className="mt-4 space-y-3">
              <div>
                <p className="text-[13px] font-medium text-white/80">{t("detail.wouldAgain")}</p>
                <div className="mt-2 flex gap-2">
                  {AGAIN.map((a) => (
                    <button key={a.key} data-testid={`again-${a.key}`} onClick={() => setWouldAgain(a.key)} className={`flex-1 rounded-xl px-2 py-2.5 text-[13px] font-medium transition-colors ${wouldAgain === a.key ? "bg-decide-forest" : "bg-decide-surface/10 hover:bg-decide-surface/15"}`}>{a.label}</button>
                  ))}
                </div>
              </div>
              <textarea data-testid="got-right-input" value={gotRight} onChange={(e) => setGotRight(e.target.value)} placeholder={t("detail.gotRight")} rows={2} className="w-full rounded-xl bg-decide-surface/10 px-4 py-3 text-[15px] placeholder:text-white/50 focus:outline-none" />
              <textarea data-testid="missed-input" value={missed} onChange={(e) => setMissed(e.target.value)} placeholder={t("detail.missed")} rows={2} className="w-full rounded-xl bg-decide-surface/10 px-4 py-3 text-[15px] placeholder:text-white/50 focus:outline-none" />
              <button data-testid="submit-review-btn" onClick={submitReview} className="w-full rounded-full bg-decide-surface text-decide-ink dark:bg-decide-forest dark:text-decide-canvas py-3 font-semibold">{t("detail.saveReflection")}</button>
            </div>
          )}
        </div>
      )}

      {d.review && (
        <div className="mb-5 rounded-2xl border border-decide-line bg-decide-surface p-4" data-testid="review-summary">
          <p className="text-[11px] font-bold uppercase tracking-wider text-decide-mute">{t("detail.yourReview")}</p>
          <p className="mt-1 font-medium text-decide-ink">{OUTCOMES.find((o) => o.key === d.review.outcome)?.label}</p>
          {d.review.would_again && <p className="mt-2 text-[14px] text-decide-slate"><span className="text-decide-ink font-medium">{t("detail.wouldAgain")} </span>{AGAIN.find((a) => a.key === d.review.would_again)?.label}</p>}
          {d.review.got_right && <p className="mt-2 text-[14px] text-decide-slate"><span className="text-decide-ink font-medium">{t("detail.rightPrefix")}</span>{d.review.got_right}</p>}
          {d.review.missed && <p className="mt-1 text-[14px] text-decide-slate"><span className="text-decide-ink font-medium">{t("detail.missedPrefix")}</span>{d.review.missed}</p>}
        </div>
      )}

      <Result analysis={d.analysis} options={d.options} decisionId={d.id} title={d.title} />

      {d.analysis?.next_action && (
        <div className="mt-8 rounded-3xl border border-decide-forest/30 bg-decide-forestLight p-6" data-testid="detail-next-action">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-decide-forest">{t("detail.nextAction")}</p>
          <p className="mt-2 text-lg font-medium leading-snug text-decide-ink">{d.analysis.next_action}</p>
          {!d.next_step_done && (
            <button data-testid="detail-mark-next-step" onClick={markStep} className="btn-accent mt-5 w-full"><Check size={18} /> {t("detail.markNextStep")}</button>
          )}
        </div>
      )}

      {!d.review_at && !d.review && (
        <button data-testid="detail-schedule-review" onClick={() => setReviewOpen(true)} className="btn-secondary mt-4 w-full"><Clock size={17} /> {t("detail.reviewLater")}</button>
      )}

      <AnimatePresence>
        {reviewOpen && (
          <>
            <motion.div className="fixed inset-0 z-50 bg-black/30" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setReviewOpen(false)} />
            <motion.div className="fixed bottom-0 inset-x-0 z-50 mx-auto max-w-lg bg-decide-canvas rounded-t-3xl border-t border-decide-line px-6 pt-3 pb-8" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }} style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 2rem)" }} data-testid="detail-review-sheet">
              <div className="mx-auto mb-5 h-1.5 w-10 rounded-full bg-decide-line" />
              <h3 className="text-xl font-semibold text-decide-ink">{t("detail.reviewWhen")}</h3>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {[{ k: "1w", l: t("flow.in1w") }, { k: "1m", l: t("flow.in1m") }, { k: "3m", l: t("flow.in3m") }, { k: "6m", l: t("flow.in6m") }].map((o) => (
                  <button key={o.k} data-testid={`detail-review-${o.k}`} onClick={() => schedule(o.k)} className="rounded-2xl border border-decide-line bg-decide-surface px-4 py-4 font-medium text-decide-ink hover:border-decide-ink transition-colors">{o.l}</button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </AppShell>
  );
}
